import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, Label, TextInput, Button } from 'flowbite-react';
import { HiMail, HiLockClosed, HiKey } from 'react-icons/hi';
import { observer } from 'mobx-react-lite';
import { authStore } from '../stores/authStore';
import { toastStore } from '../stores/toastStore';

const ResetPasswordPage: React.FC = observer(() => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const emailFromParams = searchParams.get('email') || '';

  const [email, setEmail] = useState(emailFromParams);
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  useEffect(() => {
    if (!emailFromParams) {
      toastStore.showError('メールアドレスが指定されていません');
      navigate('/forgot-password');
    }
  }, [emailFromParams, navigate]);

  const validatePassword = (password: string): boolean => {
    if (password.length < 8) {
      setPasswordError('パスワードは8文字以上である必要があります');
      return false;
    }
    if (!/[a-z]/.test(password)) {
      setPasswordError('パスワードには小文字が必要です');
      return false;
    }
    if (!/[A-Z]/.test(password)) {
      setPasswordError('パスワードには大文字が必要です');
      return false;
    }
    if (!/[0-9]/.test(password)) {
      setPasswordError('パスワードには数字が必要です');
      return false;
    }
    if (!/[^a-zA-Z0-9]/.test(password)) {
      setPasswordError('パスワードには記号が必要です');
      return false;
    }
    setPasswordError('');
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      setPasswordError('パスワードが一致しません');
      return;
    }

    if (!validatePassword(newPassword)) {
      return;
    }

    setIsLoading(true);

    try {
      const success = await authStore.confirmPasswordReset(email, code, newPassword);

      if (success) {
        toastStore.showSuccess('パスワードがリセットされました。新しいパスワードでログインしてください。');
        navigate('/login');
      } else {
        if (authStore.error) {
          toastStore.showError(authStore.error);
        }
      }
    } catch (error) {
      // Error already handled in authStore
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card>
      <form onSubmit={handleSubmit} className="space-y-4">
        <h2 className="text-2xl font-bold text-gray-900">パスワードをリセット</h2>
        <p className="text-sm text-gray-600">
          メールで送信された確認コードと新しいパスワードを入力してください。
        </p>

        <div>
          <Label htmlFor="email" value="メールアドレス" />
          <TextInput
            id="email"
            type="email"
            icon={HiMail}
            placeholder="email@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={isLoading}
          />
        </div>

        <div>
          <Label htmlFor="code" value="確認コード" />
          <TextInput
            id="code"
            type="text"
            icon={HiKey}
            placeholder="メールで送信された6桁のコード"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            required
            disabled={isLoading}
          />
        </div>

        <div>
          <Label htmlFor="newPassword" value="新しいパスワード" />
          <TextInput
            id="newPassword"
            type="password"
            icon={HiLockClosed}
            placeholder="••••••••"
            value={newPassword}
            onChange={(e) => {
              setNewPassword(e.target.value);
              if (passwordError) validatePassword(e.target.value);
            }}
            required
            disabled={isLoading}
            color={passwordError ? 'failure' : undefined}
            helperText={
              <span className="text-xs text-gray-500">
                8文字以上、大文字・小文字・数字・記号を含む必要があります
              </span>
            }
          />
        </div>

        <div>
          <Label htmlFor="confirmPassword" value="パスワード（確認）" />
          <TextInput
            id="confirmPassword"
            type="password"
            icon={HiLockClosed}
            placeholder="••••••••"
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              if (passwordError && e.target.value === newPassword) {
                setPasswordError('');
              }
            }}
            required
            disabled={isLoading}
            color={passwordError ? 'failure' : undefined}
          />
          {passwordError && (
            <p className="mt-1 text-sm text-red-600">{passwordError}</p>
          )}
        </div>

        <Button
          type="submit"
          className="w-full"
          disabled={isLoading}
          isProcessing={isLoading}
        >
          パスワードをリセット
        </Button>

        <div className="text-center">
          <button
            type="button"
            onClick={() => navigate('/login')}
            className="text-sm text-blue-600 hover:underline"
            disabled={isLoading}
          >
            ログイン画面に戻る
          </button>
        </div>
      </form>
    </Card>
  );
});

export default ResetPasswordPage;
